import React, { useState } from "react";
import {
  FaCut,
  FaHandSparkles,
  FaPaintBrush,
  FaEye,
  FaSpa,
  FaLeaf,
  FaPen,
  FaGem,
  FaStar,
  FaClock,
  FaUser,
  FaChevronRight,
} from "react-icons/fa";
import { formatCurrency } from "../utils/displayHelpers";
import "./ServiceCard.css";

/**
 * One service, one card. Used on every page that lists services to a customer.
 *
 * The icon shown when a service has no photo is chosen from the SERVICE CATEGORY, not a fixed
 * scissors — scissors on a spa's hot-stone massage is exactly the mismatch that prompted this. When
 * neither the category nor the business type says anything useful, it falls back to a neutral mark
 * rather than guessing.
 */
const CATEGORY_ICONS = [
  [/nail|manicure|pedicure/i, FaHandSparkles],
  [/make[- ]?up|cosmetic|brow-?lamination/i, FaPaintBrush],
  [/lash|brow|eye/i, FaEye],
  [/spa|massage|facial|skin|wellness|body[- ]?treatment|aromatherapy|hot[- ]?stone/i, FaSpa],
  [/wax|sugar|hair[- ]?removal|threading/i, FaLeaf],
  [/piercing/i, FaGem],
  [/tattoo/i, FaPen],
  [/braid|loc\b|locs|hair|cut|barber|styl|colou?r|weav|extension|shampoo|treatment|shave|beard/i, FaCut],
];

/** Neutral, deliberately: used when neither the service nor the business says what it is. */
const NEUTRAL_ICON = FaStar;

/**
 * Pick the icon for a service.
 *
 * @param {Object} service - needs `category` and/or `name`
 * @param {String} [businessType] - e.g. "spa", "salon", "barber" — only consulted if the service itself
 *                                  gives no clue, so a spa's "Hot Stone Massage" and its "Consultation"
 *                                  land on the same mark.
 */
export function serviceIconFor(service = {}, businessType = "") {
  const serviceText = [service.category, service.name, service.serviceType]
    .filter(Boolean)
    .join(" ");

  for (const [pattern, Icon] of CATEGORY_ICONS) {
    if (pattern.test(serviceText)) return Icon;
  }

  const typeText = String(businessType || "");
  for (const [pattern, Icon] of CATEGORY_ICONS) {
    if (pattern.test(typeText)) return Icon;
  }

  return NEUTRAL_ICON;
}

/**
 * @param {Object}   service
 * @param {Number}   [professionalCount] - shown as a pill on the thumbnail
 * @param {String}   [image]             - explicit image URL (defaults to service.serviceImage)
 * @param {String}   [pricePrefix]       - e.g. "From" when professionals charge differently
 * @param {String}   [businessType]      - used only to choose the fallback icon
 * @param {Function} onSelect            - called with the service on click/Enter
 * @param {String}   [ctaLabel]          - defaults to "Book"
 * @param {String}   [footnote]          - one-line provider context (search results: which shop/where)
 */
const ServiceCard = ({
  service,
  professionalCount = 0,
  image,
  pricePrefix = "",
  businessType = "",
  onSelect,
  ctaLabel = "Book",
  footnote = "",
}) => {
  const [imageBroken, setImageBroken] = useState(false);

  const duration = service?.longestDuration || service?.durationMinutes;
  const price =
    service?.price !== null && service?.price !== undefined
      ? service.price
      : service?.lowestPrice;
  // An explicit `image` wins (e.g. search results falling back to the shop photo); a URL that fails to
  // load drops to the icon instead of a browser broken-image glyph.
  const thumb = image || service?.serviceImage;
  const showThumb = Boolean(thumb) && !imageBroken;
  const Icon = serviceIconFor(service, businessType);

  return (
    <button
      type="button"
      className="service-tile"
      onClick={() => onSelect?.(service)}
    >
      <span className="service-tile-thumb">
        {showThumb ? (
          <img
            className="service-tile-thumb-img"
            src={thumb}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImageBroken(true)}
          />
        ) : (
          <span className="service-tile-icon">
            <Icon size={18} aria-hidden="true" />
          </span>
        )}

        {professionalCount > 0 && (
          <span
            className="service-tile-badge"
            title={`${professionalCount} professional${
              professionalCount > 1 ? "s" : ""
            } offer this service`}
          >
            <FaUser size={8} aria-hidden="true" />
            {professionalCount}
          </span>
        )}
      </span>

      <span className="service-tile-body">
        <span className="service-tile-name">{service?.name}</span>

        <span className="service-tile-meta">
          {duration > 0 && (
            <span>
              <FaClock size={10} aria-hidden="true" />
              {duration} min
            </span>
          )}
        </span>

        <span className="service-tile-foot">
          {price !== null && price !== undefined && price !== "" ? (
            <span className="service-tile-price">
              {pricePrefix && (
                <span className="service-tile-price-prefix">{pricePrefix}</span>
              )}
              {formatCurrency(price)}
            </span>
          ) : (
            <span />
          )}
          <span className="service-tile-cta">
            {ctaLabel}
            <FaChevronRight size={8} aria-hidden="true" />
          </span>
        </span>

        {footnote && <span className="service-tile-footnote">{footnote}</span>}
      </span>
    </button>
  );
};

export default ServiceCard;
